import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from './reduxHooks';
import { themeObject } from 'styles/themes/themeVariables';
import { ResolvedTheme } from 'types';
import {
  getSystemThemeMediaQuery,
  resolveThemePreference,
} from 'services/themePreference.service';
import { setResolvedTheme } from 'store/slices/themeSlice';

const setBrowserThemeColor = (theme: ResolvedTheme): void => {
  let metaThemeColor = document.querySelector<HTMLMetaElement>(
    'meta[name="theme-color"]'
  );

  if (!metaThemeColor) {
    metaThemeColor = document.createElement('meta');
    metaThemeColor.name = 'theme-color';
    document.head.appendChild(metaThemeColor);
  }

  metaThemeColor.content = themeObject[theme].primary;
};

export const useThemeWatcher = (): ResolvedTheme => {
  const dispatch = useAppDispatch();
  const { preference, resolvedTheme } = useAppSelector((state) => state.theme);

  useEffect(() => {
    const mediaQuery =
      preference === 'system' ? getSystemThemeMediaQuery() : null;
    dispatch(setResolvedTheme(resolveThemePreference(preference, mediaQuery)));

    if (preference !== 'system' || !mediaQuery) {
      return undefined;
    }

    const handleSystemThemeChange = (event: MediaQueryListEvent): void => {
      dispatch(setResolvedTheme(event.matches ? 'dark' : 'light'));
    };

    mediaQuery.addEventListener('change', handleSystemThemeChange);

    return () => {
      mediaQuery.removeEventListener('change', handleSystemThemeChange);
    };
  }, [dispatch, preference]);

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.noTransition = '';
    root.dataset.theme = resolvedTheme;
    root.dataset.themePreference = preference;
    root.style.colorScheme = resolvedTheme;
    setBrowserThemeColor(resolvedTheme);

    const animationFrame = window.requestAnimationFrame(() => {
      delete root.dataset.noTransition;
    });

    return () => {
      window.cancelAnimationFrame(animationFrame);
      delete root.dataset.noTransition;
    };
  }, [preference, resolvedTheme]);

  return resolvedTheme;
};
